import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SprsGauge from "@/components/SprsGauge";
import { ScanSearch, FileText, AlertTriangle, Radio, FileStack, ArrowRight, Loader2 } from "lucide-react";

function Stat({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="overline">{label}</span>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="font-display font-black text-3xl tracking-tight">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;

  const hasRun = data.sprs_score !== null && data.sprs_score !== undefined;

  return (
    <div className="p-8 max-w-7xl">
      <div className="mb-8">
        <div className="overline mb-1">Compliance Command Center</div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Dashboard</h1>
      </div>

      {!hasRun && (
        <div className="mb-8 bg-sky-50 border border-sky-200 rounded-md p-5 flex items-center justify-between" data-testid="empty-state">
          <div>
            <div className="font-semibold text-brand">No assessment yet</div>
            <div className="text-sm text-slate-600">Upload your policies and run your first gap analysis to get your SPRS score.</div>
          </div>
          <Link to="/app/gap" className="bg-brand text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 hover:bg-brand-secondary transition-colors">
            Run Gap Analysis <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm lg:col-span-2 flex flex-col items-center justify-center" data-testid="dash-sprs">
          <SprsGauge score={hasRun ? data.sprs_score : null} />
          <div className="text-sm text-slate-500 mt-2">{hasRun ? "Based on assessed objectives" : "Run an analysis to populate"}</div>
        </div>
        <Stat label="Audit Readiness" value={hasRun ? `${data.readiness_pct}%` : "—"} sub={`${data.objectives_total} objectives tracked`} icon={ScanSearch} testid="dash-readiness" />
        <Stat label="Open POA&M" value={hasRun ? data.open_poam : "—"} sub="items needing remediation" icon={AlertTriangle} testid="dash-poam" />
        <Stat label="Policies" value={`${data.policies_generated}/${data.policies_total}`} sub="generated" icon={FileText} testid="dash-policies" />
        <Stat label="Documents" value={data.documents} sub="ingested as evidence" icon={FileStack} testid="dash-docs" />
        <Stat label="Evidence captured" value={data.recent_evidence.length > 0 ? "Active" : "—"} sub={data.last_run_at ? "monitoring on" : "not started"} icon={Radio} testid="dash-evidence" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg tracking-tight">Recent Evidence Feed</h2>
          <Link to="/app/evidence" className="text-sm text-brand-accent font-medium">View all</Link>
        </div>
        <div className="p-6">
          {data.recent_evidence.length === 0 ? (
            <div className="text-sm text-slate-500">No evidence captured yet. Run a collector sweep from the Evidence Collector.</div>
          ) : (
            <div className="space-y-2 font-mono text-xs">
              {data.recent_evidence.map((e) => (
                <div key={e.id} className="flex items-center gap-3 text-slate-600">
                  <span className="text-success">●</span>
                  <span className="text-slate-400">{new Date(e.captured_at).toLocaleString()}</span>
                  <span className="text-brand font-sans font-medium">{e.label}</span>
                  <span className="text-slate-400 truncate">{e.sha256?.slice(0, 16)}…</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
